import React, { useState, useEffect, useCallback } from 'react';
import { ProductsTableAdapter } from '@/components/products/ProductsTableAdapter';
import { ProductGrid } from '@/components/products/ProductGrid';
import { ViewToggle } from '@/components/products/ViewToggle';
import { ProductsTableFilters } from '@/components/products/ProductsTableFilters';

import { getCategories } from '@/services/categoryService';
import { useFamilies } from '@/api/familyApi';
import { productService } from '@/services/productService';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
// i18n Step 3: Import translation hook
import { useProductstableTranslations } from '@/hooks/useProductstableTranslations';
// Import unified filter types
import {
  ProductsFilterState,
  createDefaultFilterState,
  addToMultiSelectFilter,
  removeFromMultiSelectFilter,
  isValueSelectedInMultiSelect,
} from '@/types/filters';

type ViewMode = 'list' | 'grid';

export default function ProductsPage() {
  // i18n Step 3: Initialize translation hook
  const translations = useProductstableTranslations();

  // i18n Step 3: Simplified constants - use literal values for ViewMode to avoid initialization issues
  const constants = React.useMemo(
    () => ({
      viewModes: {
        list: 'list' as ViewMode,
        grid: 'grid' as ViewMode,
      },
      filterValues: {
        all: translations.getFilterValueText('all') as 'all',
        active: translations.getFilterValueText('active') as 'active',
        inactive: translations.getFilterValueText('inactive') as 'inactive',
      },
      storage: {
        productViewModeKey: translations.getStorageText('productViewModeKey'),
      },
      queryKeys: {
        categories: translations.getQueryKeyText('categories'),
        tags: translations.getQueryKeyText('tags'),
        brands: translations.getQueryKeyText('brands'),
      },
    }),
    [translations]
  );

  const [viewMode, setViewMode] = useState<ViewMode>(constants.viewModes.list);

  // Add search state here
  const [searchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // LIFTED FILTER STATE - shared by both List and Grid views - using unified filter state
  const [filters, setFilters] = useState<ProductsFilterState>(() =>
    createDefaultFilterState(constants.viewModes.list, '', constants.filterValues)
  );

  // Update filters when search term changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: debouncedSearchTerm,
      page_size: viewMode === constants.viewModes.list ? 10 : 50,
    }));
  }, [debouncedSearchTerm, viewMode, constants.viewModes.list]);

  // SHARED FILTER HANDLERS - using unified types with auto-detect multi-select support
  const handleFilterChange = useCallback(
    (columnId: string, value: any) => {
      setFilters(prev => {
        let nextValue = value;

        // Auto-detect multi-select behavior for brand, family, and category based on value type
        const isMultiSelectFilter =
          columnId === 'brand' || columnId === 'family' || columnId === 'category';
        const isArrayValue = Array.isArray(value);

        if (isMultiSelectFilter && isArrayValue) {
          // Value is already an array from the UI component, use it directly
          nextValue = value;
        } else if (isMultiSelectFilter && !isArrayValue) {
          // Single value for a multi-select filter, convert to array behavior
          const currentValue = prev[columnId as keyof ProductsFilterState];
          const isSelected = isValueSelectedInMultiSelect(
            currentValue,
            value,
            constants.filterValues.all
          );

          if (isSelected) {
            nextValue = removeFromMultiSelectFilter(
              currentValue,
              value,
              constants.filterValues.all
            );
          } else {
            nextValue = addToMultiSelectFilter(currentValue, value, constants.filterValues.all);
          }
        }

        const next = { ...prev, [columnId as keyof ProductsFilterState]: nextValue };

        // Clean up empty values
        if (
          nextValue === '' ||
          nextValue === constants.filterValues.all ||
          (Array.isArray(nextValue) && nextValue.length === 0)
        ) {
          delete (next as any)[columnId];
        }

        return next;
      });
    },
    [constants.filterValues.all]
  );

  const handleClearFilters = useCallback(() => {
    setFilters(
      createDefaultFilterState(
        viewMode === constants.viewModes.list ? 'list' : 'grid',
        debouncedSearchTerm,
        constants.filterValues
      )
    );
  }, [viewMode, debouncedSearchTerm, constants.viewModes.list, constants.filterValues]);

  // Note: ProductsTableAdapter and ProductGrid components handle their own data fetching internally

  // Get ALL categories from API (not filtered by current products)
  const { data: allCategories = [] } = useQuery({
    queryKey: [constants.queryKeys.categories],
    queryFn: getCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get ALL families from API (not filtered by current products)
  const { data: allFamilies = [], isLoading: isFamiliesLoading } = useFamilies();

  // Get ALL tags from API (not filtered by current products)
  const { data: allTags = [] } = useQuery({
    queryKey: [constants.queryKeys.tags],
    queryFn: () => productService.searchTags(''), // Empty string gets all tags
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get ALL brands from API (not filtered by current products)
  const { data: allBrands = [] } = useQuery({
    queryKey: [constants.queryKeys.brands],
    queryFn: () => productService.searchBrands(''), // Empty string gets all brands
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create category options with IDs as values for grid view (all available categories)
  const gridCategoryOptions = React.useMemo(() => {
    return allCategories
      .map(category => ({
        label: category.name,
        value: String(category.id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allCategories]);

  // Create brand options for grid view (all available brands)
  const gridBrandOptions = React.useMemo(() => {
    return allBrands.map(brand => brand.value).sort();
  }, [allBrands]);

  // Create tag options for grid view (all available tags)
  const gridTagOptions = React.useMemo(() => {
    return allTags.map(tag => tag.label).sort();
  }, [allTags]);

  // Load saved view mode preference
  useEffect(() => {
    const savedViewMode = localStorage.getItem(constants.storage.productViewModeKey);
    if (savedViewMode === constants.viewModes.grid || savedViewMode === constants.viewModes.list) {
      setViewMode(savedViewMode as ViewMode);
    }
  }, [constants.storage.productViewModeKey, constants.viewModes.grid, constants.viewModes.list]);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem(constants.storage.productViewModeKey, viewMode);
  }, [viewMode, constants.storage.productViewModeKey]);

  // Toggle page scroll based on view mode
  useEffect(() => {
    // Find the main element by ID for more reliable selection
    const mainElement = document.getElementById('dashboard-main');
    if (!mainElement) return;

    if (viewMode === constants.viewModes.list) {
      // List view: disable page scroll to allow table's own scroll
      mainElement.classList.add('overflow-hidden');
      mainElement.classList.remove('overflow-y-auto');
      mainElement.classList.add('flex', 'flex-col');
    } else {
      // Grid view: restore page scroll
      mainElement.classList.remove('overflow-hidden');
      mainElement.classList.add('overflow-y-auto');
      mainElement.classList.remove('flex', 'flex-col');
    }

    // Cleanup: restore original classes when component unmounts
    return () => {
      mainElement.classList.remove('overflow-hidden', 'flex', 'flex-col');
      mainElement.classList.add('overflow-y-auto');
    };
  }, [viewMode, constants.viewModes.list]);

  return (
    <div className="hide-x-scrollbar flex min-h-0 min-w-0 flex-1 flex-col">
      {/* Table Toolbar */}
      <div className="flex flex-col items-start justify-between gap-1 border-b p-1 sm:flex-row sm:items-center sm:gap-1">
        <div className="flex w-full items-center space-x-2 sm:w-auto">
          {/* Search and filter removed */}
        </div>
        <div className="flex items-center space-x-2">
          <ViewToggle view={viewMode} onViewChange={setViewMode} />
          {/* Refresh and New Product buttons removed as per instructions */}
        </div>
      </div>

      {/* SHARED FILTER BAR - renders once for both views */}
      <div className="mb-4">
        {viewMode === constants.viewModes.grid && (
          <ProductsTableFilters
            columns={[]} // Empty for now - will be populated by individual components
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            table={null as any} // Pass null since we don't need TanStack table instance here
            uniqueCategories={gridCategoryOptions}
            uniqueTags={gridTagOptions}
            uniqueBrands={gridBrandOptions}
            families={allFamilies}
            isFamiliesLoading={isFamiliesLoading}
          />
        )}
      </div>

      {/* Main Content */}
      <section
        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        data-tour-target="products-table"
      >
        {/* ONE self-contained scroll region */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {viewMode === constants.viewModes.grid ? (
            <ProductGrid
              filters={filters}
              products={[]}
              loading={false}
              error={null}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              table={undefined}
              columns={[]}
              uniqueCategories={gridCategoryOptions}
              uniqueTags={gridTagOptions}
              uniqueBrands={gridBrandOptions}
              families={allFamilies}
              isFamiliesLoading={isFamiliesLoading}
            />
          ) : (
            <ProductsTableAdapter
              viewMode={constants.viewModes.list}
              filters={filters}
              hideTopSearch={true}
              hideTopControls={false}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />
          )}
        </div>
      </section>
    </div>
  );
}
